import React, { useState } from 'react'
import { Modal } from '@components/Basic/Modal'
import { Button } from '@components/Basic/ButtonNbe'
import { useTranslation } from 'react-i18next'
import { trackEvent } from '@app/dataTracking'
import {
  ModalWrapper,
  ModalHeader,
  ModalSubtitle,
  InputLabel,
  Input,
  ButtonsWrapper,
  AlertBox,
  AlertIcon,
  AlertContent,
  FormRow,
  FormField,
  Textarea,
} from './styled'
import { InputSelect } from '@components/Form/InputSelect'
import { useRequestIntegration } from '@app/api/requestIntegration'
import {
  FaExclamationTriangle,
  FaInfoCircle,
  FaCheckCircle,
} from 'react-icons/fa'
import { useAppDispatch } from '@app/store/hooks'
import { sendToast } from '@app/store/actions/ApplicationConfigurationActions'

interface ModalIntegrationRequestProps {
  isOpen: boolean
  onCloseModal: () => void
}

type ResponseStatus = 'new' | 'waiting_list' | 'existing' | null

export const ModalIntegrationRequest: React.FC<ModalIntegrationRequestProps> =
  ({ isOpen, onCloseModal }) => {
    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [integrationName, setIntegrationName] = useState('')
    const [role, setRole] = useState<'source' | 'destination'>('source')
    const [description, setDescription] = useState('')
    const [responseStatus, setResponseStatus] = useState<ResponseStatus>(null)
    const [foundAppName, setFoundAppName] = useState<string | null>(null)
    const [joinedWaitlist, setJoinedWaitlist] = useState(false)

    const { mutate: requestIntegration, isLoading } = useRequestIntegration()

    const roleOptions = [
      { value: 'source', label: t('nbee.integrationRequest.roleSource') },
      { value: 'destination', label: t('nbee.integrationRequest.roleDestination') },
    ]

    const handleClose = () => {
      setIntegrationName('')
      setRole('source')
      setDescription('')
      setResponseStatus(null)
      setFoundAppName(null)
      setJoinedWaitlist(false)
      onCloseModal()
    }

    const handleSubmit = () => {
      if (!integrationName.trim()) return

      setJoinedWaitlist(false)
      setResponseStatus(null)
      setFoundAppName(null)

      requestIntegration(
        { integrationName: integrationName.trim(), role, description: description.trim() },
        {
          onSuccess: (response) => {
            const status = response.data?.status as ResponseStatus
            setResponseStatus(status)
            setFoundAppName(response.data?.appName || null)

            if (status === 'new') {
              trackEvent({
                eventName: 'IntegrationRequestSubmitted',
                feature: 'NBEE',
                step: 'Apps',
                params: {
                  custom: {
                    integrationName: integrationName.trim(),
                    role,
                  },
                },
              })
              dispatch(
                sendToast({
                  title: t('nbee.integrationRequest.successTitle'),
                  message: t('nbee.integrationRequest.successMessage'),
                  color: 'positive',
                })
              )
              handleClose()
            }
          },
          onError: () => {
            dispatch(
              sendToast({
                title: 'Error',
                message: t('common.genericError'),
                color: 'negative',
              })
            )
          },
        }
      )
    }

    const handleJoinWaitlist = () => {
      trackEvent({
        eventName: 'IntegrationWaitlistJoined',
        feature: 'NBEE',
        step: 'Apps',
        params: {
          custom: {
            integrationName: integrationName.trim(),
          },
        },
      })
      setJoinedWaitlist(true)
    }

    const renderAlert = () => {
      if (joinedWaitlist) {
        return (
          <AlertBox $variant='success'>
            <AlertIcon>
              <FaCheckCircle />
            </AlertIcon>
            <AlertContent>
              {t('nbee.integrationRequest.addedToWaitlist')}
            </AlertContent>
          </AlertBox>
        )
      }

      if (responseStatus === 'existing') {
        return (
          <AlertBox $variant='warning'>
            <AlertIcon>
              <FaExclamationTriangle />
            </AlertIcon>
            <AlertContent>
              {foundAppName
                ? t('nbee.integrationRequest.integrationAvailableWithName', { appName: foundAppName })
                : t('nbee.integrationRequest.integrationAvailable')}
            </AlertContent>
          </AlertBox>
        )
      }

      if (responseStatus === 'waiting_list') {
        return (
          <AlertBox $variant='info'>
            <AlertIcon>
              <FaInfoCircle />
            </AlertIcon>
            <AlertContent>
              {t('nbee.integrationRequest.integrationComingSoon')}{' '}
              <a onClick={handleJoinWaitlist}>
                {t('nbee.integrationRequest.joinWaitlist')}
              </a>{' '}
              {t('nbee.integrationRequest.toBeNotified')}
            </AlertContent>
          </AlertBox>
        )
      }

      return null
    }

    return (
      <Modal isOpen={isOpen} onCloseModal={handleClose}>
        <ModalWrapper>
          <ModalHeader>{t('nbee.integrationRequest.title')}</ModalHeader>
          <ModalSubtitle>{t('nbee.integrationRequest.subtitle')}</ModalSubtitle>

          {renderAlert()}

          <FormRow>
            <FormField $flex={2}>
              <InputLabel>{t('nbee.integrationRequest.inputLabel')}</InputLabel>
              <Input
                type='text'
                value={integrationName}
                onChange={(e) => setIntegrationName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
              />
            </FormField>
            <FormField>
              <InputSelect
                name='role'
                $label={t('nbee.integrationRequest.roleLabel')}
                $options={roleOptions}
                value={role}
                onChange={(e) =>
                  setRole(e.target.value as 'source' | 'destination')
                }
              />
            </FormField>
          </FormRow>

          <FormField style={{ marginBottom: '1.5rem' }}>
            <InputLabel>{t('nbee.integrationRequest.descriptionLabel')}</InputLabel>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('nbee.integrationRequest.descriptionPlaceholder')}
              maxLength={1000}
            />
          </FormField>

          <ButtonsWrapper>
            <Button type='button' $variant='secondary' onClick={handleClose}>
              {t('common.close')}
            </Button>
            <Button
              type='button'
              $variant='primary'
              onClick={handleSubmit}
              disabled={!integrationName.trim() || isLoading}
            >
              {t('nbee.integrationRequest.submit')}
            </Button>
          </ButtonsWrapper>
        </ModalWrapper>
      </Modal>
    )
  }
